import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { RootState } from "../../store.interface";

import { Classified } from "@/shared/interfaces/classified/Classified.interface";
import { MyClassifiedsStoreState, MyClassifiedsApiError } from "@/shared/interfaces/MyClassifieds.interface";

import { getMyClassifieds } from "@/shared/scripts/ClassifiedsApi";
import { getObjetIndexByKey } from "@/shared/scripts/helpers/array";
import { STATUSES_MATCHES } from "@/shared/scripts/helpers/constants/classifiedTypes";

import store from "../..";
import { i18n } from "@/shared/scripts/helpers/translation/i18n";
import { app } from "@/main";

const isStatusLike = (classifiedStatus: string, status: string): boolean => STATUSES_MATCHES[status].includes(classifiedStatus);

const state = {
    myClassifieds: [],
    deactivatedClassified: {} as Classified,
    deletedClassified: {} as Classified,
    classifiedToUpgrade: {} as Classified,
    classifiedToExtend: {} as Classified,
    classifiedToReactivate: {} as Classified,
    classifiedToActivate: {} as Classified,
    myClassifiedSelected: {} as Classified,
    sort: "lastActionDate",
    filterStatusType: "ALL",
    filterTransactionType: "ALL",
    filterPropertyType: "ALL",
    searchImmowebCode: "",
    isLoading: false,
    hasError: false,
    apiErrors: [],
};

const mutations: MutationTree<MyClassifiedsStoreState> = {
    setClassifieds: function(state: MyClassifiedsStoreState, classifieds: Array<Classified>) {
        state.myClassifieds = classifieds;
    },
    addClassifieds: function(state: MyClassifiedsStoreState, classified: Classified) {
        state.myClassifieds.push(classified);
    },
    unshiftClassified: function(state: MyClassifiedsStoreState, classified: Classified) {
        state.myClassifieds.unshift(classified);
    },
    replaceClassified: function(state: MyClassifiedsStoreState, classified: Classified) {
        if (classified.id) {
            const classifiedIndex = getObjetIndexByKey(state.myClassifieds as Array<Classified>, "id", classified.id);

            if (classifiedIndex !== -1) {
                state.myClassifieds[classifiedIndex] = classified;
            }
        }
    },
    removeClassifieds: function(state: MyClassifiedsStoreState, classified: Classified) {
        state.myClassifieds = state.myClassifieds.filter(item => item.id !== classified.id);
    },
    setClassifiedToDeactivate: function(state: MyClassifiedsStoreState, classified: Classified) {
        state.deactivatedClassified = classified;
    },
    setClassifiedToDelete: function(state: MyClassifiedsStoreState, classified: Classified) {
        state.deletedClassified = classified;
    },
    setClassifiedToUpgrade: function(state: MyClassifiedsStoreState, classified: Classified) {
        state.classifiedToUpgrade = classified;
    },
    setClassifiedToExtend: function(state: MyClassifiedsStoreState, classified: Classified) {
        state.classifiedToExtend = classified;
    },
    setClassifiedToReactivate: function(state: MyClassifiedsStoreState, classified: Classified) {
        state.classifiedToReactivate = classified;
    },
    setClassifiedToActivate: function(state: MyClassifiedsStoreState, classified: Classified) {
        state.classifiedToActivate = classified;
    },
    setSort: function(state: MyClassifiedsStoreState, sort: string) {
        state.sort = sort;
    },
    setFilterTransactionType: function(state: MyClassifiedsStoreState, filterTransactionType: string){
        state.filterTransactionType = filterTransactionType;
    },
    setFilterStatusType: function(state: MyClassifiedsStoreState, filterStatusType: string) {
        state.filterStatusType = filterStatusType;
    },
    setFilterPropertyType: function(state: MyClassifiedsStoreState, filterPropertyType: string) {
        state.filterPropertyType = filterPropertyType;
    },
    setSearchImmowebCode: function(state: MyClassifiedsStoreState, searchImmowebCode: string) {
        state.searchImmowebCode = searchImmowebCode;
    },
    setLoading: function(state: MyClassifiedsStoreState, isLoading: boolean) {
        state.isLoading = isLoading;
    },
    setError: function(state: MyClassifiedsStoreState, hasError: boolean) {
        state.hasError = hasError;
    },
    setClassifiedApiError: function(state: MyClassifiedsStoreState, errors: Array<MyClassifiedsApiError>) {
        state.apiErrors = errors;
    },
    setMyClassifiedSelected: function(state: MyClassifiedsStoreState,  classified: Classified) {
        state.myClassifiedSelected = classified;
    },
    resetClassifiedApiErrors: (state: MyClassifiedsStoreState) => {
        state.apiErrors = [];
    },
    updateClassifiedStore: (state: MyClassifiedsStoreState, {pictures, id}: any) => {
        if(state.myClassifieds) {
            const myClassified = state.myClassifieds.find((classified: Classified) => {
                return classified.id === id;
            });
            if(myClassified) {
                myClassified.pictures = pictures;
            }
        }
    }
};

const actions: ActionTree<MyClassifiedsStoreState, RootState> = {
    fetchClassifieds: function({ commit }) {
        commit("setLoading", true);
        commit("setError", false);

        getMyClassifieds().then(classifieds => {
            classifieds.sort((a, b) => {
                const dateA = new Date(a.lastActionDate);
                const dateB = new Date(b.lastActionDate);
                return dateB.getTime() - dateA.getTime();
            });

            commit("setClassifieds", classifieds);
            commit("setLoading", false);
        }).catch(error => {
            console.error(error);
            commit("setLoading", false);
            commit("setError", true);
            app.dialog.alert(i18n.t("server_not_responding_error").toString());
            throw new Error(error);
        });
    },
};

const getters: GetterTree<MyClassifiedsStoreState, RootState> = {
    getClassifieds: (state: MyClassifiedsStoreState): Classified[] => {
        return state.myClassifieds;
    },

    filteredClassifieds: (state: MyClassifiedsStoreState): Classified[] => {
        return state.myClassifieds
            .filter((item: Classified) => {
                const matchesStatus = state.filterStatusType === "ALL" || isStatusLike(item.status, state.filterStatusType);
                const matchesTransactionType = state.filterTransactionType === "ALL" || state.filterTransactionType === item.transaction.type;
                const matchesPropertyType = state.filterPropertyType === "ALL" || state.filterPropertyType === item.property?.type;
                const matchesImmowebCode = !state.searchImmowebCode ||
                    (item.legacyId && item.legacyId.toString().includes(state.searchImmowebCode));

                return matchesStatus && matchesTransactionType && matchesPropertyType && matchesImmowebCode;
            })
            .sort((a, b) => {
                // sort key: postalCode
                if (state.sort === "postalCode") {
                    const postcodeA = parseInt(a.property?.location?.address?.postalCode as string);
                    const postcodeB = parseInt(b.property?.location?.address?.postalCode as string);
                    return postcodeA - postcodeB;
                }

                // sort key: lastActionDate, creationDate
                const dateA = new Date(a[state.sort]);
                const dateB = new Date(b[state.sort]);
                return dateB.getTime() - dateA.getTime();
            });
    },

    getClassifiedById: (state: MyClassifiedsStoreState) => (id: string): boolean => {
        const myClassified = state.myClassifieds.find((classified: Classified) => {
            return classified.id === id;
        });

        if (!myClassified) {
            return false;
        }

        store.commit("FormModule/setClassifiedId", myClassified.id);
        store.dispatch("FormModule/addValue", {"path": "property", "value": myClassified.property});
        store.dispatch("FormModule/addValue", {"path": "transaction", "value": myClassified.transaction});
        store.commit("FormModule/addValueUI", {"path": "currentClassifiedStatus", "value": myClassified.status});
        store.commit("PicturesModule/setPictures", myClassified.pictures ?? []);

        if (myClassified.customerId) {
            store.commit("CustomersModule/setCurrentCustomer", { id: myClassified.customerId });
        }

        return true;
    },

    draftsAndPublishable: (state: MyClassifiedsStoreState): Classified[] => {
        return state.myClassifieds.filter(({ status }: Classified) => status === "DRAFT" || status === "PUBLISHABLE");
    },

    hasClassifiedApiError: (state: MyClassifiedsStoreState) => (key: string): boolean => {
        const error = state.apiErrors.find((error) => {
            return error.property == key;
        });

        return !!error;
    },

    getClassifiedToDeactivate: (state: MyClassifiedsStoreState): Classified => {
        return state.deactivatedClassified;
    },
    getClassifiedToDelete: (state: MyClassifiedsStoreState): Classified => {
        return state.deletedClassified;
    },
    getClassifiedToUpgrade: (state: MyClassifiedsStoreState): Classified => {
        return state.classifiedToUpgrade;
    },
    getClassifiedToExtend: (state: MyClassifiedsStoreState): Classified => {
        return state.classifiedToExtend;
    },
    getClassifiedToReactivate: (state: MyClassifiedsStoreState): Classified => {
        return state.classifiedToReactivate;
    },
    getMyClassifiedSelected: (state: MyClassifiedsStoreState): Classified => {
        return state.myClassifiedSelected;
    },
};

const classified: Module<MyClassifiedsStoreState, RootState> = {
    namespaced: true,
    state: state,
    mutations: mutations,
    actions: actions,
    getters: getters,
};

export default classified;
